import { useState, useEffect, useRef } from "react"
import { useParams } from "react-router-dom"
import { doc, getDoc } from "firebase/firestore"
import { db } from "../../firebase"
import CountryHero from "../../components/CountryHero"
import CountryTabs from "../../components/CountryTabs"
import SEO from "../../components/common/SEO"
import FastFacts from "../../components/tabs/FastFacts"
import TopUniversities from "../../components/tabs/TopUniversities"
import Admissions from "../../components/tabs/Admissions"
import Scholarships from "../../components/tabs/Scholarships"
import Visa from "../../components/tabs/Visa"
import CostOfLiving from "../../components/tabs/CostOfLiving"
import WorkOpportunities from "../../components/tabs/WorkOpportunities"
import Faqs from "../../components/tabs/Faqs"
import { motion, useScroll, useSpring } from "framer-motion"
import "./country.css"

const Country = () => {
  const { id } = useParams()
  const [activeTab, setActiveTab] = useState("FAST FACTS")
  const [countryData, setCountryData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false)
  const [isTabsFixed, setIsTabsFixed] = useState(false)
  const heroRef = useRef(null)
  const { scrollYProgress } = useScroll()
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  })

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      setIsHeaderScrolled(scrollY > 50)

      // Calculate when tabs should become fixed
      // Hero (60vh) - Header height (approx 70-90px)
      if (heroRef.current) {
        const heroHeight = heroRef.current.offsetHeight
        setIsTabsFixed(scrollY > heroHeight - 80)
      }
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const sectionRefs = {
    "FAST FACTS": useRef(null),
    "TOP UNIVERSITIES": useRef(null),
    "ADMISSIONS": useRef(null),
    "SCHOLARSHIPS": useRef(null),
    "VISA": useRef(null),
    "COST OF LIVING": useRef(null),
    "WORK OPPORTUNITIES": useRef(null),
    "FAQS": useRef(null),
  }

  const formatCountryName = (name) => {
    return name
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const getCountryFlagCode = (countryName) => {
    const flagCodes = {
      uk: "197374", usa: "197484", ireland: "197567", canada: "197430",
      germany: "197571", dubai: "197558", france: "197560", europe: "197615",
      italy: "197626", netherlands: "197441", poland: "197529", spain: "197593",
      australia: "197507", "new-zealand": "197589", sweden: "197564", denmark: "197540",
    }
    return flagCodes[countryName.toLowerCase()] || "197374"
  }

  const fetchCountryData = async () => {
    try {
      setLoading(true)
      const countryId = id.toLowerCase().replace(/\s+/g, "-")
      const countryRef = doc(db, "countries", countryId)
      const countrySnap = await getDoc(countryRef)

      if (countrySnap.exists()) {
        setCountryData({ id: countrySnap.id, ...countrySnap.data() })
      } else {
        setCountryData({
          id: countryId,
          name: formatCountryName(id),
          flagUrl: `https://cdn-icons-png.flaticon.com/128/197/${getCountryFlagCode(id)}.png`,
        })
      }
    } catch (err) {
      console.error("Error fetching country data:", err)
      setError("Failed to load country data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      fetchCountryData()
    }
  }, [id])

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-140px 0px -60% 0px",
      threshold: [0, 0.1, 0.2, 0.3]
    }

    const observerCallback = (entries) => {
      // Sort entries by their position to handle rapid scrolling
      const sortedEntries = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

      if (sortedEntries.length > 0) {
        const topEntry = sortedEntries[0];
        const sectionLabel = Object.keys(sectionRefs).find(
          (key) => sectionRefs[key].current === topEntry.target
        )
        if (sectionLabel) {
          setActiveTab(sectionLabel)
        }
      }
    }

    const observer = new IntersectionObserver(observerCallback, observerOptions)

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) observer.observe(ref.current)
    })

    return () => {
      Object.values(sectionRefs).forEach((ref) => {
        if (ref.current) observer.unobserve(ref.current)
      })
    }
  }, [loading])

  if (loading) {
    return (
      <div className="loading-container">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="loader-spinner"
        />
        <span>Discovering {formatCountryName(id)}...</span>
      </div>
    )
  }

  if (error) {
    return <div className="error-container">{error}</div>
  }

  const sections = [
    { id: "fast-facts", label: "FAST FACTS", Component: FastFacts },
    { id: "top-universities", label: "TOP UNIVERSITIES", Component: TopUniversities },
    { id: "admissions", label: "ADMISSIONS", Component: Admissions },
    { id: "scholarships", label: "SCHOLARSHIPS", Component: Scholarships },
    { id: "visa", label: "VISA", Component: Visa },
    { id: "cost-of-living", label: "COST OF LIVING", Component: CostOfLiving },
    { id: "work-opportunities", label: "WORK OPPORTUNITIES", Component: WorkOpportunities },
    { id: "faqs", label: "FAQS", Component: Faqs },
  ]

  return (
    <div className="country-container">
      <SEO
        title={`Study in ${formatCountryName(id)} - Education Consultancy`}
        description={`Study in ${formatCountryName(id)} with WELSH EDUCATION GROUP. Expert guidance for ${formatCountryName(id)} student visa, university admission, and study abroad opportunities.`}
        url={`/country/${id}`}
      />

      <motion.div className="progress-bar" style={{ scaleX }} />

      <div ref={heroRef}>
        <CountryHero
          country={countryData.name || formatCountryName(id)}
          flagUrl={countryData.flagUrl}
          heroImage={countryData.heroImage}
        />
      </div>

      <CountryTabs
        activeTab={activeTab}
        country={countryData.name || formatCountryName(id)}
        isHeaderScrolled={isHeaderScrolled}
        isFixed={isTabsFixed}
      />

      <div className="sections-wrapper">
        {sections.map(({ id, label, Component }) => (
          <section
            key={id}
            id={id}
            ref={sectionRefs[label]}
            className="country-section"
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <Component countryData={countryData} />
            </motion.div>
          </section>
        ))}
      </div>

      {/* WhatsApp Button */}
      <motion.a
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        href="https://wa.me/9115400982"
        className="whatsapp-button"
        target="_blank"
        rel="noopener noreferrer"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white">
          <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
        </svg>
      </motion.a>
    </div>
  )
}

export default Country
